// ═════════════════════════════════════════════════════════════════════════════
// Live Review — the meeting agenda, built from the SERVER's remarks list.
//
// ⚠️ THIS FILE IS INTENTIONALLY IDENTICAL IN TWO REPOSITORIES (coach + CEO).
// See `LiveReviewSocketStore.js`'s header for the full reasoning and the
// obligation that comes with it: change one copy, change both in the same pass.
// Kept dependency-free for that reason — no imports at all, so nothing here can
// resolve differently in the two apps.
//
// WHY THIS REPLACES `buildReviewSummary`
//
// The coach's async review screen derives its summary panel client-side by
// walking sections and picking out anything flagged or commented. That is the
// right design there: the only data it has is the review tree.
//
// It is the wrong design here, and not by a little. `12-live-review-fe-handoff.md`
// §6 ships an authoritative `remarks[]` array with a real per-remark `discussed`
// flag, plus `remarks_remaining` / `total_remarks` / `discussed_count`, and it
// warns that `remarks_updated` "always" accompanies `item_status_updated`
// "because accepting an item can silently shrink the remarks list."
//
// So a client-side derivation would disagree with the server the first time
// anyone accepted an item — and `remarks_remaining` is what gates the Continue
// action, which means the two participants could see a different number of
// outstanding points, and one of them could see Continue enabled while the
// other saw it disabled, in the middle of a live meeting. This file therefore
// only ever RESHAPES what the server sent; it never decides what belongs on the
// agenda and never computes a count.
// ═════════════════════════════════════════════════════════════════════════════

// Must match Strategy Canvas's own `ITEM_KIND` values in BOTH repos — the
// shared `SummaryGroups` presentational component compares against these exact
// strings, so a local copy with different values would render every row as a
// plain block and silently drop the badges.
//
// Declared here rather than imported because the two repos keep Canvas in
// different places, and this file has to stay import-free to stay identical.
// If Canvas ever changes these strings, this breaks visibly (badges vanish)
// rather than subtly, which is the failure mode to prefer.
export const AGENDA_ITEM_KIND = {
  BLOCK: "block",
  SECTION: "section",
  OVERALL: "overall",
};

// ⚠️ These are CSS MODIFIER NAMES, not colour words. `CanvasBand.scss` and
// `SummaryPanel.scss` each define exactly `&--teal`, `&--dark`, `&--core` and
// `&--dna`, and the component interpolates whatever it's given straight into a
// class name (`cb-band--${color}`). So an invented value like "blue" or "amber"
// produces a class that matches no rule and the band renders unstyled — silently,
// with no console warning.
//
// This list is therefore a copy of `ExecutionBlueprintReviewHelpers`'
// `SECTION_COLORS`, in the same order, and both apps' Live Review screens now
// call this function rather than their own local copy — so the two sides cannot
// drift into different band colours, which on this screen would be a visible
// disagreement between two people looking at the same blueprint.
//
// Inlined rather than imported for the same reason nothing else here is
// imported: this file has to stay byte-identical across two repos.
const SECTION_COLORS = ["teal", "dark", "core", "dna"];

export const agendaSectionColor = (index) =>
  SECTION_COLORS[index % SECTION_COLORS.length];

const OVERALL_GROUP_KEY = "__overall__";

// The pill every undiscussed block row wears — see `status` below for why the
// agenda reports the MEETING's state rather than the item's verdict. The string
// is the backend's own (`08-fe-handoff.md` §2) and is what both apps'
// `SummaryItem` tests against, for the label and for the pending row style.
//
// Inlined rather than imported, like `AGENDA_ITEM_KIND` above: this file stays
// import-free so the two repos' copies can stay byte-identical.
const STATUS_TO_DISCUSS = "to_discuss";

// ⚠️ `REMARK_TYPE_COMMENT` was deleted here on 2026-09-03. It distinguished
// `remark_type: "comment"` (a COACH COMMENT on an item) from `"item"` (the
// item's own to-discuss verdict) — both arrive in the same `remarks[]` array
// carrying an `execution_blueprint_item_id`, so the type was the only thing
// separating them.
//
// It existed for exactly one reason: suppressing the status pill on comment
// rows. The agenda no longer distinguishes the two — every block row reports
// the same meeting state — so nothing here needs to tell them apart.
// `LiveReviewSocketStore`'s `REMARK_TYPE` still carries the strings for the
// code that acts on remarks.

const hasText = (value) => !!(value && String(value).trim());

// The DOM anchor an agenda row scrolls to. One function so the agenda and the
// canvas can never disagree about the id — they did not share this before, and
// a mismatch shows up as a click that highlights nothing.
export const agendaTargetFor = (remark) => {
  if (!remark) return null;
  if (remark.execution_blueprint_item_id != null) {
    return `item-${remark.execution_blueprint_item_id}`;
  }
  if (remark.section_key) return `section-${remark.id}`;
  return "overall";
};

// Build a lookup of item id → { sectionKey, label } so a remark can be placed
// in its section and titled.
//
// This is needed because a remark does NOT carry its section: the §6 example
// shows a comment remark with `execution_blueprint_item_id: 812` and
// `section_key: null`. Only the sections tree knows which WIG item 812 lives
// in, so grouping has to go through it.
//
// ⚠️ `reviewStatus: item.review_status ?? null` was dropped from this index on
// 2026-09-03. It existed only to lend the item's verdict to its agenda rows,
// and the agenda now reports the meeting's state instead — see `status` in
// `buildLiveAgenda`. Nothing else read it.
const indexItems = (sections) => {
  const byItemId = {};
  (sections || []).forEach((section) => {
    (section.items || []).forEach((item) => {
      byItemId[String(item.execution_blueprint_item_id)] = {
        sectionKey: section.section_key,
        label: item.label,
      };
    });
  });
  return byItemId;
};

/**
 * Reshape `snapshot.remarks` into the group tree `SummaryGroups` renders:
 * `{ key, band, name, color, items[] }`, where each item is
 * `{ id, kind, title, status, comments, discussed, remark }`.
 *
 * `discussed` is passed straight through because `SummaryItem` already renders
 * a "Discussed" pill from it — that support was built for Strategy Canvas's own
 * live review, so the agenda gets the right treatment with no component change
 * and, importantly, no design change.
 *
 * `remark` is attached to every row so a click handler can mark it discussed
 * without having to find it again by id. That matters: `mark_remark_discussed`
 * wants the REMARK's id, and re-deriving it from a row would put a remark id
 * and an item id in the same scope — exactly the mix-up the socket store's
 * emitter signature is shaped to prevent.
 */
export const buildLiveAgenda = (snapshot) => {
  if (!snapshot) return [];
  const sections = [...(snapshot.sections || [])].sort(
    (a, b) => (a.sequence ?? 0) - (b.sequence ?? 0),
  );
  const itemIndex = indexItems(sections);

  // Seed a group per section, in canvas order, and keep the order stable
  // regardless of what order remarks arrive in.
  const groups = sections.map((section, index) => ({
    key: section.section_key,
    // No eyebrow: the band and the name were the same text, so it printed twice.
    band: null,
    name: section.label,
    color: agendaSectionColor(index),
    items: [],
  }));
  const groupByKey = {};
  groups.forEach((group) => {
    groupByKey[group.key] = group;
  });

  const overallGroup = {
    key: OVERALL_GROUP_KEY,
    band: null,
    name: "Whole blueprint",
    // ⚠️ "core", not "grey". This is the same trap the SECTION_COLORS comment
    // above warns about, and the first version of this file walked straight into
    // it: `SummaryPanel.js` renders `sp-section-rail--${group.color}`, and
    // `SummaryPanel.scss` defines only `--teal/--dark/--core/--dna`. "grey" is a
    // real colour word and a plausible choice for an ungrouped bucket, which is
    // exactly why it slipped through - it produces a class matching no rule, so
    // the rail renders unstyled with no error anywhere.
    //
    // "core" is what `buildReviewSummary` already uses for its identical "Whole
    // blueprint" group (ExecutionBlueprintReviewSummary.js), so this also keeps
    // the two agendas looking the same.
    color: "core",
    items: [],
  };

  (snapshot.remarks || []).forEach((remark) => {
    const itemId =
      remark.execution_blueprint_item_id != null
        ? String(remark.execution_blueprint_item_id)
        : null;
    const known = itemId ? itemIndex[itemId] : null;

    // An item-scoped remark whose item we can't find is NOT dropped. It goes to
    // the overall group instead, because silently omitting it would make the
    // visible agenda shorter than the server's own `remarks_remaining` — and
    // the coach would then be told there are points left with nothing on screen
    // to point at. Better to show it unplaced than to hide it.
    const targetGroup = known
      ? groupByKey[known.sectionKey] || overallGroup
      : remark.section_key && groupByKey[remark.section_key]
        ? groupByKey[remark.section_key]
        : overallGroup;

    const isSectionScoped = !itemId && !!remark.section_key;
    const isOverall = !itemId && !remark.section_key;

    const kind = isOverall
      ? AGENDA_ITEM_KIND.OVERALL
      : isSectionScoped
        ? AGENDA_ITEM_KIND.SECTION
        : AGENDA_ITEM_KIND.BLOCK;

    // Prefer the sections tree's label over the remark's own copy of it: the
    // tree is what the canvas renders, and an item edited during this session
    // updates there first. `item_label` is the server's snapshot of the label at
    // the time the remark was written.
    const title = known?.label || remark.item_label || remark.label || "";

    targetGroup.items.push({
      id: agendaTargetFor(remark),
      kind,
      title,
      // ── An undiscussed block row is "To discuss". Full stop. ─────────────
      // ⚠️ THIS IS THE MEETING'S STATE, NOT THE ITEM'S VERDICT (2026-09-03,
      // on instruction). Every agenda row for a specific WIG reads "To
      // discuss" until it is marked discussed, at which point `SummaryItem`'s
      // own `discussed` branch takes over and shows "Discussed".
      //
      // The rule moved three times in three days, so here is why this one is
      // right where the other two were not:
      //
      //   1. It used to show the item's own `review_status`, borrowed onto
      //      every remark row.
      //   2. On 2026-09-01 it was nulled for `remark_type: "comment"` rows,
      //      because an item with three comments repeated the same pill three
      //      times and the pill stopped reading as a state.
      //   3. On 2026-09-03 the status was put back on block comment rows, on
      //      instruction — which reintroduced (1)'s real bug, below.
      //
      // **The bug in showing the verdict.** Canvas offers "Mark discussed"
      // when `status === ACCEPTED && comments.length && openRemarks.length` —
      // a row the solo review ACCEPTED while leaving a note. EXW's row mirrors
      // it condition for condition (`canMarkDiscussed`). Borrowing the verdict
      // made the agenda show a green "Accepted" pill on exactly that row: the
      // canvas asking the coach to act, and the agenda beside it saying the
      // row was already settled. Worse, `remarks_remaining === 0` is what
      // gates Complete, so the coach could be blocked from finishing while
      // EVERY agenda row read "Accepted" or "Discussed" and nothing said which
      // one was holding it up — in the one panel whose entire job is to say
      // what is left.
      //
      // **Why "To discuss" is not a compromise.** An agenda row IS a remark,
      // and the agenda only ever contains flagged items and comments — an item
      // accepted cleanly produces no remark and so has no row here at all.
      // Every row on this panel is therefore something still to talk about,
      // and until it is discussed that is precisely its state. The item's
      // verdict is still visible where it belongs: on the canvas row itself.
      //
      // A consequence worth knowing: "Accepted" can no longer appear in the
      // agenda. That is the point, not a regression.
      //
      // It also earns the row its pending treatment for free — both apps'
      // `SummaryItem` adds `sp-item--discuss` on `!discussed && status ===
      // "to_discuss"` — and it needs NO component change, which matters more
      // than it looks: the CEO app has its own `_design/SummaryPanel` while the
      // coach app aliases `SummaryGroups` to Strategy Canvas's shared copy, so
      // any fix made in a component would have reached one app and an
      // unrelated feature in the other. Deciding it here reaches both.
      //
      // Scoped rows stay null: a section or overall comment has no item behind
      // it, and both components already refuse a pill there anyway.
      status: kind === AGENDA_ITEM_KIND.BLOCK ? STATUS_TO_DISCUSS : null,
      // ⚠️ FALSE ON SECTION AND OVERALL COMMENTS (2026-09-01, on instruction),
      // so those rows carry no pill at all — no status (they never had one) and
      // now no "Discussed" either. A scoped comment is the coach's remark about
      // a whole section or the whole blueprint; the badge above it already says
      // which, and a pill there is decoration rather than a state anyone acts
      // on. Only a BLOCK row — one item, one decision — still shows it.
      //
      // ⚠️ This is a DISPLAY suppression written into the row, not the server's
      // value. It is done here because it is the only lever that reaches both
      // apps: the CEO app has its own `_design/SummaryPanel`, the coach app
      // aliases `SummaryGroups` to Strategy Canvas's shared one, and Canvas's
      // own `buildAgendaGroups` sets `discussed` on its scoped rows too — so
      // editing the component would fix one app and change an unrelated feature
      // in the other.
      //
      // ⚠️ Safe only because these rows go nowhere but `SummaryGroups` in both
      // apps (`agendaGroups` has exactly one consumer). Everything that acts on
      // a remark — `openRemarks`, `mark_remark_discussed`, the discussed tag on
      // the canvas row — reads `snapshot.remarks` directly, and the untouched
      // `remark` attached below still carries the true value. If an agenda row's
      // `discussed` ever gains a second reader, this has to move.
      discussed: kind === AGENDA_ITEM_KIND.BLOCK && remark.discussed === true,
      comments: hasText(remark.comment_text)
        ? [{ coach_comment_id: remark.id, comment_text: remark.comment_text }]
        : [],
      remark,
    });
  });

  const populated = groups.filter((group) => group.items.length > 0);
  if (overallGroup.items.length > 0) populated.push(overallGroup);
  return populated;
};

// The counts come from the server, always — never from the shape above. A local
// `groups.reduce(...)` would be a second source of truth for a number both
// participants read off each other's screens, and would drift from
// `remarks_remaining` the moment an accept resolved a remark server-side.
//
// Defaulted to 0 rather than left undefined so a snapshot that hasn't landed
// renders "0 of 0" instead of "undefined".
export const agendaCounts = (snapshot) => ({
  remaining: snapshot?.remarks_remaining ?? 0,
  total: snapshot?.total_remarks ?? 0,
  discussed: snapshot?.discussed_count ?? 0,
});

// Everything discussed → the Continue action is available (§3.7). Reads the
// server's counter, not the list length: a remark can be resolved server-side
// by an accept without the list changing shape in the same tick.
export const allRemarksDiscussed = (snapshot) => {
  if (!snapshot) return false;
  return (snapshot.remarks_remaining ?? 0) === 0;
};
